import { motion } from 'framer-motion';
import { AlertTriangle, ShieldCheck, X } from 'lucide-react';

/**
 * Advice, not a wall. A flagged cardio movement opens this sheet; the user can
 * read the risk and still choose it. Styled to match the cardio swap sheet.
 */
export function CardioRiskSheet({ open, modality, advisory, onCancel, onAccept, accepting }) {
  if (!open || !advisory) return null;

  return (
    <div className="fixed inset-0 z-[70] flex flex-col" data-testid="cardio-risk-sheet">
      <div className="flex-1 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 32, stiffness: 320 }}
        className="bg-[#0E0E0E] border-t border-white/10 rounded-t-3xl max-h-[88vh] overflow-y-auto"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 96px)' }}
      >
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-white/15" />
        </div>

        <div className="flex items-start justify-between px-5 pt-2 pb-4">
          <div className="flex items-start gap-3 pr-3">
            <span className="w-9 h-9 shrink-0 rounded-full bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
              <AlertTriangle size={16} className="text-amber-400" strokeWidth={2.5} />
            </span>
            <div>
              <div className="text-[10px] font-heading font-black uppercase tracking-[0.18em] text-amber-400">
                Coach's advice
              </div>
              <h3 className="font-heading text-xl uppercase tracking-tight text-white leading-tight mt-0.5">
                {advisory.headline || modality?.name}
              </h3>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="w-8 h-8 shrink-0 rounded-full bg-[#1F1F1F] flex items-center justify-center active:scale-90"
            data-testid="cardio-risk-close"
          >
            <X size={15} className="text-white/60" />
          </button>
        </div>

        <div className="px-5 space-y-3">
          <p className="text-sm text-white/65 leading-relaxed" data-testid="cardio-risk-reason">
            {advisory.risk}
          </p>
          <div className="flex gap-2.5 rounded-xl bg-white/[0.04] border border-white/[0.07] p-3">
            <ShieldCheck size={15} className="shrink-0 mt-0.5 text-accent" strokeWidth={2.5} />
            <p className="text-sm text-white/55 leading-relaxed">{advisory.safer}</p>
          </div>
          {advisory.dose_note && (
            <p className="text-xs text-white/40 leading-relaxed" data-testid="cardio-risk-dose-note">
              {advisory.dose_note}
            </p>
          )}
        </div>

        <div className="px-5 pt-5 space-y-2.5">
          <button
            onClick={onCancel}
            className="w-full py-3.5 rounded-xl bg-accent text-accent-foreground font-heading font-black uppercase tracking-wider text-sm active:scale-[0.98] transition-transform"
            data-testid="cardio-risk-pick-safer"
          >
            Pick something safer
          </button>
          <button
            onClick={onAccept}
            disabled={accepting}
            className="w-full py-3.5 rounded-xl border border-amber-400/40 text-amber-400 font-heading font-bold uppercase tracking-wider text-sm active:scale-[0.98] transition-transform disabled:opacity-50"
            data-testid="cardio-risk-use-anyway"
          >
            {accepting ? 'Unlocking…' : 'Use it anyway'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
